import { TableColumnForTableExport } from '@doss/common-all'
import { RxArrowDown, RxArrowUp } from 'react-icons/rx'
import { Collapsible } from '~/@/components/ui/collapsible'
import { TableSortInput } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

interface ExportSortPreviewProps {
  sorts: TableSortInput[]
  columns: TableColumnForTableExport[]
}

/**
 * Displays a collapsible preview of sorts that will be applied during export.
 * Shows the column name and direction for each sort.
 */
export function ExportSortPreview({ sorts, columns }: ExportSortPreviewProps) {
  const { t } = useTypedTranslation()

  if (sorts.length === 0) {
    return (
      <div className="rounded bg-gray-2 p-3">
        <span className="text-sm text-gray-9">{t('table.export.no_sorts')}</span>
      </div>
    )
  }

  const columnsById = new Map(columns.map((col) => [col.id, col]))

  return (
    <Collapsible
      defaultOpen
      title={
        <span className="text-xs text-gray-9">
          {t('table.export.sorts_being_exported')} ({sorts.length})
        </span>
      }
    >
      <div className="mt-2 flex flex-col gap-2 rounded bg-gray-2 p-3">
        {sorts.map((sort, index) => {
          const column = columnsById.get(sort.columnId)
          const columnName = column?.name ?? sort.columnId
          const isAsc = sort.direction === 'ASC'

          return (
            <div key={index} className="flex items-center gap-2">
              {isAsc ? (
                <RxArrowUp className="text-gray-9" size={16} />
              ) : (
                <RxArrowDown className="text-gray-9" size={16} />
              )}
              <span className="text-sm font-medium">{columnName}</span>
              <span className="text-sm text-gray-9">
                {isAsc ? t('table.export.sort_ascending') : t('table.export.sort_descending')}
              </span>
            </div>
          )
        })}
      </div>
    </Collapsible>
  )
}
